import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Fade,
  Paper,
  Divider
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import LoadingImage from '../../components/LoadingImage';

const MersinFrigoDorseServisi: React.FC = () => {
  useEffect(() => {
    document.title = "Mersin Lojistik Sektöründe Frigo Dorse Bakım ve Servisi - Kale Treyler";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Mersin MIG tır parkı dorse servisi, frigo kasa periyodik bakım süreçleri, araç yalıtımı ve kapı lastiği değişimi gibi servis ihtiyaçları detayları.");
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', "Mersin frigo dorse servisi, MIG tır parkı dorse tamiri, dorse periyodik bakım, soğutmalı dorse kasa servisi Mersin");
    }

    const canonicalLink = (document.querySelector('link[rel="canonical"]') || document.createElement('link')) as HTMLLinkElement;
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', 'https://kaletreyler.com/blog/mersin-frigo-dorse-servisi-rehberi');
    if (!document.head.contains(canonicalLink)) {
      document.head.appendChild(canonicalLink);
    }
  }, []);

  return (
    <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
      <Container maxWidth="lg">
        <Fade in timeout={800}>
          <Box>
            <Button
              component={RouterLink}
              to="/blog"
              startIcon={<ArrowBack />}
              sx={{ mb: 4, textTransform: 'none', fontWeight: 'bold' }}
            >
              Blog Yazılarına Dön
            </Button>

            <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, border: '1px solid', borderColor: 'grey.200', borderRadius: 4 }}>
              <Typography variant="overline" color="secondary" sx={{ fontWeight: 'bold', letterSpacing: 1.5 }}>
                Dorse Bakım
              </Typography>
              <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800, mt: 1, mb: 4, fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.2 }}>
                Mersin Lojistik Sektöründe Frigo Dorse Bakım ve Servis İhtiyaçları
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Yayınlanma Tarihi: <strong>5 Temmuz 2026</strong>
                </Typography>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body2" color="text.secondary">
                  Yazar: <strong>Kale Treyler Editör</strong>
                </Typography>
              </Box>

              <Box sx={{ borderRadius: 3, overflow: 'hidden', mb: 6, boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                <LoadingImage
                  src="/18.jpeg"
                  alt="Mersin'de Frigo Dorse Servis İstasyonumuz"
                  style={{ width: '100%', height: 'auto', maxHeight: '500px', objectFit: 'cover' }}
                />
              </Box>

              <Box sx={{ typography: 'body1', color: 'text.primary', lineHeight: 1.8 }}>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  Mersin, jeopolitik konumu, gelişmiş liman altyapısı ve tır filoları ile Türkiye'nin en büyük lojistik ve uluslararası taşımacılık merkezlerinden biridir. Özellikle narenciye, taze gıda ve kimyasal ürünlerin ihraç edildiği bu bölgede, nakliye operasyonlarının aksamadan yürümesi frigo dorselerin teknik sağlığına bağlıdır. Mersin Akdeniz ilçesinde bulunan <RouterLink to="/iletisim" style={{ color: '#1e3a8a', fontWeight: 'bold', textDecoration: 'underline' }}>Kale Treyler Frigo</RouterLink> servis istasyonumuzda, bölgedeki lojistik ağının aksamadan devam etmesine teknik destek sağlıyoruz.
                </Typography>

                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mt: 4, mb: 2, color: 'primary.main' }}>
                  Neden Frigo Dorse Periyodik Bakımı Yapılmalıdır?
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  Soğutmalı dorseler normal tenteli dorselere kıyasla çok daha hassas yapılara sahiptir. Yalıtımlı sandviç paneller, kapı fitilleri ve arka kilit mekanizmaları sürekli titreşim altında olduğundan zamanla gevşeyebilir veya aşınabilir. Düzenli olarak yapılan <strong>dorse periyodik bakım</strong> çalışmaları, olası yalıtım kayıplarını ve hasarları önceden tespit ederek yolda kalma ve yükün bozulma risklerini azaltmaya katkı sağlar. Menteşe yağlamaları, lastik conta kontrolleri ve köşe birleşim silikonları gibi detaylar bu bakımlarda tek tek gözden geçirilir.
                </Typography>

                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mt: 4, mb: 2, color: 'primary.main' }}>
                  MIG Tır Parkı Bölgesinde Hızlı Dorse Servisi
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  Mersin liman çıkışlarında ve otoban bağlantı noktalarındaki tır parklarında bekleyen şoförler için zaman en değerli kaynaktır. Sefere çıkacak veya limana girecek araçların anlık dorse sorunlarına müdahale edilmesi kritik önem taşır. Bizler <strong>MIG tır parkı dorse tamiri</strong> ve bakım ihtiyaçlarına yönelik konumlandırılmış servis noktamızda, frigo dorse panelleri, kapı revizyonları, iç yalıtım sızdırmazlığı ve yedek parça değişim hizmetlerini hızlı ve profesyonel işçilikle sunuyoruz.
                </Typography>

                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mt: 4, mb: 2, color: 'primary.main' }}>
                  Mersin'de Profesyonel Dorse Tamir İstasyonu
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                  Nakliye operasyonlarınızda maksimum araç kullanılabilirliği elde etmek ve dorse yalıtım standartlarınızı korumak için tecrübeli teknisyenlerden destek almanız önemlidir. Bölgedeki tır filoları için geliştirdiğimiz profesyonel <strong>Mersin frigo dorse servisi</strong> hizmetlerimizle, araç içi yalıtımdan kapı kilidine, çift kat panel montajından dorse yedek parçalarına kadar tüm ihtiyaçları tek bir servis noktasında topluyoruz.
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', borderLeft: '4px solid', borderColor: 'primary.main', pl: 2, py: 1, bgcolor: 'grey.100' }}>
                  Not: Sitemizde yer alan tüm blog içerikleri lojistik sektörü çalışanları için genel bilgilendirme amacıyla hazırlanmıştır. Aracınızın periyodik bakım takvimi ve detaylı iş listesi için lütfen servis yetkilimizle iletişime geçiniz.
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default MersinFrigoDorseServisi;
